/**
 * The app's entry point for filing a bug report.
 *
 * Rendered in the places a user reaches for when something is wrong: the
 * persistent headers, for problems that never throw (a wrong layout, a
 * simulation producing implausible numbers), and the error boundary, for
 * problems that do.
 *
 * It is a plain link, not a dialog: GitHub's own issue form is a better place
 * to compose the report than anything rendered here, and it doubles as the
 * confirmation step — the prefilled diagnostics arrive in an editable
 * textarea, so the user reviews and can delete them before submitting.
 */
import React from "react";

import { buildIssueUrl, type ReportDetails } from "../report-issue";

interface ReportIssueLinkProps {
  /** Diagnostics to prefill into the report. */
  readonly details?: ReportDetails;
  /** Link text. Defaults to the wording used in the persistent headers. */
  readonly children?: React.ReactNode;
  /** Extra class name, so each host can size and place the link itself. */
  readonly className?: string;
}

const ReportIssueLink: React.FC<ReportIssueLinkProps> = ({
  details = {},
  children = "Report an issue",
  className,
}) => {
  return (
    <a
      className={
        className === undefined
          ? "report-issue-link"
          : `report-issue-link ${className}`
      }
      href={buildIssueUrl(details)}
      target="_blank"
      rel="noopener noreferrer"
      title="Report a problem as a GitHub issue, prefilled with version details"
    >
      {children}
    </a>
  );
};

export default ReportIssueLink;
